<template>
  <div>
    <el-button
        v-if="piiAuthorized"
        type="primary"
        class="nav-action"
        round
        style="padding: 10px 25px;"
        @click="openDialog()">
      PLC Request
    </el-button>
    <el-dialog
        :visible.sync="dialogVisible"
        :fullscreen="true"
        height="100%"
        width="100%">
      <el-card>
        <div
            slot="header"
            class="clearfix">
          <span class="header-item">Devices</span>
        </div>
        <el-table
            ref="filterTable"
            v-loading="loading.status && [null, 'devices'].includes(loading.entity)"
            :data="tableDevices"
            style="width: 100%">
          <el-table-column
              prop="kind"
              label="Kinds"
              :filters="typeOfDevices"
              :filter-method="filterTypeOfDevice"
              sortable
              width="250">
            <template slot-scope="scope">
              {{ startCase(scope.row.kind) }}
            </template>
          </el-table-column>
          <el-table-column
              prop="market"
              label="Market"
              width="150">
            <template slot-scope="scope">
              {{ getLocationMarket(scope.row) | capitalize }}
            </template>
          </el-table-column>
          <el-table-column
              prop="name"
              label="Name"
              :filters="deviceNames"
              :filter-method="filterDeviceName"
              sortable
              width="400">
            <template slot-scope="scope">
              {{ startCase(scope.row.description) }}
            </template>
          </el-table-column>
          <el-table-column
              v-if="delayedFulfillmentEnabled"
              label="Fulfillment"
              width="150">
            <template slot-scope="scope">
              <i
                  v-if="http.coverage.device.fetching && !loading.status"
                  class="el-icon-loading"> </i>
              <div v-else>
                <el-popover
                    placement="bottom"
                    trigger="hover"
                    :disabled="!showFulfillmentReason(scope.row.eligibility.isDelayedFulfillment)">
                  <el-tag
                      slot="reference"
                      :style="{ cursor: scope.row.eligibility.isDelayedFulfillment ? 'help' : 'default' }"
                      :type="fulfillmentTagType(scope.row.eligibility.isDelayedFulfillment)"
                      size="mini">
                    {{ getFulfillmentStatus(scope.row.eligibility.isDelayedFulfillment) }}
                  </el-tag>
                  <FulfillmentReasons :reason="get(scope.row, 'eligibility.delayedFulfillmentReason', '')" />
                </el-popover>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              prop="warranty_status"
              label="Warranty Status"
              width="150">
            <template slot-scope="scope">
              <i
                  v-if="http.coverage.device.fetching && !loading.status"
                  class="el-icon-loading"> </i>
              <div v-else>
                <el-popover
                    v-if="http.coverage.device.loaded && !!scope.row.warranty"
                    style="display: inline-block; width: 300px;"
                    placement="bottom"
                    trigger="hover">
                  <el-tag
                      slot="reference"
                      style="cursor: help;"
                      :type="warrantyTagType(scope.row)"
                      size="mini">
                    {{ get(scope.row.warranty, "status", "Not Available") }}
                  </el-tag>
                  <WarrantyInfo :data="scope.row.warranty" />
                </el-popover>
                <span
                    v-else
                    :style="{ color: colors.outOfWarranty }">Not Available</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              label="Eligibility"
              width="170">
            <template slot-scope="scope">
              <i
                  v-if="http.coverage.device.fetching && !loading.status"
                  class="el-icon-loading"> </i>
              <div v-else>
                <template>
                  <EligibilityTags :eligibility="scope.row.eligibility" />
                </template>
              </div>
            </template>
          </el-table-column>


          <el-table-column
              prop="device_id"
              label="Device Id"
              width="170" />
          <el-table-column
              prop="state"
              label="state"
              :filters="deviceState"
              :filter-method="filterDeviceState"
              width="100"
              sortable>
            <template slot-scope="scope">
              {{ startCase(scope.row.state) }}
            </template>
          </el-table-column>
          <el-table-column
              width="360"
              label="Request">
            <template slot-scope="scope">
              <span>
                <WithVariable
                    v-slot:default="{ repl = scope.row.eligibility.replacement }"
                    class="inline">
                  <el-tooltip
                      placement="bottom"
                      :disabled="!!repl.isUserAllowed && !repl.noBypassable"
                      :content="serviceTooltip(repl)">
                    <el-button
                        v-acl.show="{ permission: 'create', on: 'ReplaceRequest' }"
                        size="small"
                        round
                        :disabled="!repl.isUserAllowed || repl.noBypassable"
                        :class="{ 'disabled': !repl.isUserAllowed || repl.noBypassable }"
                        @click="replaceForm(scope.row)">
                      Replace
                    </el-button>
                  </el-tooltip>
                </WithVariable>
              </span>
              <span>
                <WithVariable
                    v-slot:default="{ refund = scope.row.eligibility.refund }"
                    class="inline">
                  <el-tooltip
                      placement="bottom"
                      :disabled="!!refund.isUserAllowed && !refund.noBypassable"
                      :content="serviceTooltip(refund)">
                    <el-button
                        v-acl.show="{ permission: 'create', on: 'RefundRequest' }"
                        size="small"
                        round
                        :disabled="!refund.isUserAllowed || refund.noBypassable"
                        :class="{ 'disabled': !refund.isUserAllowed || refund.noBypassable }"
                        @click="refundForm(scope.row)">
                      Refund
                    </el-button>
                  </el-tooltip>
                </WithVariable>
              </span>
              <span>
                <WithVariable
                    v-slot:default="{ parts = scope.row.eligibility.parts }"
                    class="inline">
                  <el-tooltip
                      placement="bottom"
                      :disabled="!!parts.isUserAllowed && !parts.noBypassable"
                      :content="serviceTooltip(parts)">
                    <el-button
                        v-acl.show="{ permission: 'create', on: 'PartsRequest' }"
                        size="small"
                        round
                        :disabled="!parts.isUserAllowed || parts.noBypassable"
                        :class="{ 'disabled': !parts.isUserAllowed || parts.noBypassable }"
                        @click="partsForm(scope.row)">
                      Parts
                    </el-button>
                  </el-tooltip>
                </WithVariable>
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div class="user-activity-table__footer-pagination">
          <el-pagination
              layout="total, prev, pager, next"
              :current-page.sync="currentDevicePage"
              :page-size="pageSize"
              :total="totalDevices"
              @current-change="updateDisplayedDevices" />
        </div>
      </el-card>

      <el-card>
        <div
            slot="header"
            class="clearfix">
          <span class="header-item">Accessories</span>
        </div>
        <el-table
            ref="filterTable"
            v-loading="loading.status && [null, 'accessories'].includes(loading.entity)"
            :data="tableAccessories"
            style="width: 100%">
          <el-table-column
              prop="kind"
              label="Kind"
              :filters="typeOfAccessories"
              :filter-method="filterTypeOfDevice"
              sortable
              width="250">
            <template slot-scope="scope">
              {{ startCase(scope.row.kind) }}
            </template>
          </el-table-column>
          <el-table-column
              prop="market"
              label="Market"
              width="150">
            <template slot-scope="scope">
              {{ getLocationMarket(scope.row) | capitalize }}
            </template>
          </el-table-column>
          <el-table-column
              prop="name"
              label="Name"
              :filters="accessoryNames"
              :filter-method="filterDeviceName"
              sortable
              width="400">
            <template slot-scope="scope">
              {{ startCase(scope.row.name) }}
            </template>
          </el-table-column>
          <el-table-column
              label="Eligibility"
              width="170">
            <template slot-scope="scope">
              <i
                  v-if="http.coverage.accessory.fetching && !loading.status"
                  class="el-icon-loading"> </i>
              <div v-else>
                <template>
                  <EligibilityTags :eligibility="scope.row.eligibility" />
                </template>
              </div>
            </template>
          </el-table-column>
          <el-table-column
              prop="device_id"
              label="Device Id"
              width="170" />
          <el-table-column
              prop="state"
              label="state"
              :filters="accessoryStates"
              :filter-method="filterDeviceState"
              width="100"
              sortable>
            <template slot-scope="scope">
              {{ startCase(scope.row.state) }}
            </template>
          </el-table-column>
          <el-table-column
              width="360"
              label="Request">
            <template slot-scope="scope">
              <span>
                <WithVariable
                    v-slot:default="{ repl = scope.row.eligibility.replacement }"
                    class="inline">
                  <el-tooltip
                      placement="bottom"
                      :disabled="!!repl.isUserAllowed && !repl.noBypassable"
                      :content="serviceTooltip(repl)">
                    <el-button
                        v-acl.show="{ permission: 'create', on: 'ReplaceRequest' }"
                        size="small"
                        round
                        :disabled="!repl.isUserAllowed || repl.noBypassable"
                        :class="{ 'disabled': !repl.isUserAllowed || repl.noBypassable }"
                        @click="replaceForm(scope.row)">
                      Replace
                    </el-button>
                  </el-tooltip>
                </WithVariable>
              </span>
              <span>
                <WithVariable
                    v-slot:default="{ refund = scope.row.eligibility.refund }"
                    class="inline">
                  <el-tooltip
                      placement="bottom"
                      :disabled="!!refund.isUserAllowed && !refund.noBypassable"
                      :content="serviceTooltip(refund)">
                    <el-button
                        v-acl.show="{ permission: 'create', on: 'RefundRequest' }"
                        size="small"
                        round
                        :disabled="!refund.isUserAllowed || refund.noBypassable"
                        :class="{ 'disabled': !refund.isUserAllowed || refund.noBypassable }"
                        @click="refundForm(scope.row)">
                      Refund
                    </el-button>
                  </el-tooltip>
                </WithVariable>
              </span>
              <span>
                <WithVariable
                    v-slot:default="{ parts = scope.row.eligibility.parts }"
                    class="inline">
                  <el-tooltip
                      placement="bottom"
                      :disabled="!!parts.isUserAllowed && !parts.noBypassable"
                      :content="serviceTooltip(parts)">
                    <el-button
                        v-acl.show="{ permission: 'create', on: 'PartsRequest' }"
                        size="small"
                        round
                        :disabled="!parts.isUserAllowed || parts.noBypassable"
                        :class="{ 'disabled': !parts.isUserAllowed || parts.noBypassable }"
                        @click="partsForm(scope.row)">
                      Parts
                    </el-button>
                  </el-tooltip>
                </WithVariable>
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div class="user-activity-table__footer-pagination">
          <el-pagination
              layout="total, prev, pager, next"
              :current-page.sync="currentAccessoriesPage"
              :page-size="pageSize"
              :total="totalAccessories"
              @current-change="updateDisplayedAccessories" />
        </div>
      </el-card>
    </el-dialog>
    <!-- NEW REPLACEMENT FORM DIALOG-->
    <replacement-form
        v-if="currentDevice && replacementFormVisible"
        ref="replacement"
        :visible.sync="replacementFormVisible"
        :device="currentDevice"
        :eligibility="currentDevice.eligibility"
        :is-supervisor="isSupervisor" />

    <replacement-modal-form
        v-if="currentDevice"
        ref="replacement"
        :device="currentDevice"
        @closed-form="currentDevice = null" />
    <parts-modal-form
        v-if="currentDevice"
        ref="parts"
        :device="currentDevice"
        @closed-form="currentDevice = null" />
    <refund-modal-form
        v-if="currentDevice"
        ref="refund"
        :device="currentDevice"
        @closed-form="currentDevice = null" />

    <out-of-warranty-warning-modal
        ref="outOfWarrantyWarningModal"
        @on-close="currentDevice = null" />
  </div>
</template>

<script>
import ReplacementModalForm from "@/containers/CustomerRequest/ReplacementModalForm";
import RefundModalForm from "@/containers/CustomerRequest/RefundModalForm";
import PartsModalForm from "@/containers/CustomerRequest/PartsModalForm";
import OutOfWarrantyWarningModal from "@/containers/CustomerRequest/OutOfWarrantyWarningModal";
import { get, sortBy, startCase, uniqBy } from "lodash";

import axios from "axios";

import {
  DeviceWarrantyAndEligibilityPolicy,
  Warranty,
} from "@/helpers/DeviceWarrantyAndEligibilityPolicy";

import { createNamespacedHelpers } from "vuex";
import ReplacementForm from "../../containers/Customer/ReplacementWidget/ReplacementForm.vue";
import WarrantyInfo from "@/components/Shared/WarrantyInfo.vue";
import EligibilityReasons from "@/components/Shared/EligibilityReasons.vue";
import EligibilityTags from "@/components/Shared/EligibilityTags.vue";
import FulfillmentReasons from "@/components/Shared/FulfillmentReasons.vue";

const { mapActions: deviceActions } = createNamespacedHelpers("db/devices");
const { mapGetters: accountGetters } = createNamespacedHelpers("db/account");
const { mapGetters: sharedGetters } = createNamespacedHelpers("shared");
// From Warranty Service codes: https://code.amazon.com/packages/RingWarrantyService/blobs/mainline/--/utils/constants.go
const DEVICE = "device";
const ACCESSORY = "accessory";

export default {
  name: "PlcButton",
  components: {
    WarrantyInfo,
    EligibilityReasons,
    ReplacementForm,
    ReplacementModalForm,
    RefundModalForm,
    PartsModalForm,
    OutOfWarrantyWarningModal,
    EligibilityTags,
    FulfillmentReasons,
  },
  data() {
    return {
      DEVICE,
      ACCESSORY,
      replacementFormVisible: false,
      warrantyEndsAt: "",
      oowReason: "",
      dialogVisible: false,
      loading: {
        loaded: false,
        status: false,
        entity: null,
      },
      customerPlcInfo: {},
      requestForms: {
        replacement: false,
        refund: false,
        parts: false,
      },
      // devices: [],
      deviceRegistry: {},
      tableDevices: [],
      accessories: [],
      tableAccessories: [],
      currentDevicePage: 1,
      currentAccessoriesPage: 1,
      pageSize: 10,
      totalAccessories: 0,
      totalDevices: 0,
      currentDevice: null,
      error: null,
      availableMarkets: {},
      firstSetupDate: "",
      http: {
        coverage: {
          accessory: {
            loaded: false,
            fetching: false,
          },
          device: {
            loaded: false,
            fetching: false,
          },
        },
      },
      eligibility: {},
      colors: {
        inWarranty: "#009f29",
        outOfWarranty: "#d71b00",
      },
    };
  },
  computed: {
    ...sharedGetters([
      "userHasPermission",
      "markets",
      "delayedFulfillmentEnabled",
      "delayedFulfillmentTipsEnabled",
    ]),
    ...accountGetters(["customer", "piiAuthorized"]),
    devices() {
      return Object.values(this.deviceRegistry);
    },
    customerPLCDevicesEndPoint() {
      return `/v3/customers/${this.customer.id}/plc_devices/devices`;
    },
    customerPLCDeviceInfoEndPoint() {
      return `/v3/customers/${this.customer.id}/plc_devices/information`;
    },
    deviceState() {
      return this.createFilter(this.tableDevices, "state");
    },
    typeOfDevices() {
      return this.createFilter(this.tableDevices, "type");
    },
    typeOfAccessories() {
      const accessories = this.tableAccessories.filter(
        (item) => item.isAccessory
      );
      return this.createFilter(accessories, "type");
    },
    deviceNames() {
      return this.createFilter(this.tableDevices, "name");
    },
    accessoryNames() {
      return this.createFilter(this.tableAccessories, "name");
    },
    accessoryStates() {
      return this.createFilter(this.tableAccessories, "state");
    },
    isSupervisor() {
      return this.$store.getters["auth/isSupervisor"];
    },
    newReplacementFlow() {
      return !!this.userHasPermission({
        permission: "newReplacementFlow",
        on: "NewUI",
      });
    },
    OOWFlow() {
      return !!this.userHasPermission({
        permission: "OOWFlow",
        on: "NewUI",
      });
    },
    currentPageDeviceIds() {
      const startIndex = (this.currentDevicePage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.devices.slice(startIndex, endIndex).map((device) => ({
        device_id: device.device_id,
        doorbot_id: device.doorbot_id,
        location_id: device.location_id,
      }));
    },
  },

  watch: {
    dialogVisible: function (value) {
      if (value && this.customer) {
        this.fetchCustomerDevices();
      }
    },
    "loading.status": {
      handler: function (newVal, oldVal) {
        if (oldVal === true && newVal === false) {
          this.$nextTick(async () => {
            await this.fetchDevicesWarrantyAndEligibility(
              DEVICE,
              this.currentPageDeviceIds
            );
          });
        }
      },
    },
  },
  methods: {
    ...deviceActions(["getWarrantyAndEligibilityStatus"]),
    serviceTooltip(service) {
      if (!!service && service.noBypassable) {
        let message = get(service, "nonBypassableRestrictions[0]");
        return `Non-bypassable restriction: ${message}`;
      }

      return service?.ineligibilityReasons?.length > 0
        ? "Contact your supervisor"
        : "";
    },
    warrantyTagType(entity) {
      return entity.warranty?.in_warranty ? "success" : "danger";
    },
    showFulfillmentReason(status) {
      return this.delayedFulfillmentTipsEnabled && status;
    },
    fulfillmentTagType(isDelayed) {
      return isDelayed ? "danger" : "success";
    },
    getFulfillmentStatus(isDelayed) {
      return isDelayed ? "Delayed" : "Standard";
    },
    createFilter(data, property) {
      let list = data.map((item) => ({
        value: item[property],
        text: startCase(item[property]),
      }));
      let criteria = (item) => item.value;
      return sortBy(uniqBy(list, criteria), criteria);
    },
    startCase,
    get,
    deviceLocation(locationId) {
      const locations = this.$store.getters["db/account/locations"];

      return (
        locations.filter((l) => l.location_id === locationId)[0] || {
          market: "n/a",
        }
      );
    },
    getLocationMarket(device) {
      let market = get(
        this.deviceLocation(device.location_id),
        "plc_market.market_name",
        "N/A"
      );
      return device.isAccessory ? "N/A" : market;
    },
    openDialog() {
      this.dialogVisible = true;
    },
    filterDeviceName(value, row) {
      return row.name === value;
    },
    filterTypeOfDevice(value, row) {
      return row.kind === value;
    },
    filterDeviceState(value, row) {
      return row.state === value;
    },
    updateDisplayedAccessories: function () {
      this.loading.entity = ACCESSORY;
      const startIndex = (this.currentAccessoriesPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      this.tableAccessories = this.accessories.slice(startIndex, endIndex);
      this.$nextTick(() => (this.loading.entity = null));

      const ids = this.tableAccessories.map((a) => ({
        device_id: a.device_id,
      }));
      this.fetchDevicesWarrantyAndEligibility(ACCESSORY, ids);
    },
    async updateDisplayedDevices() {
      this.loading.status = true;
      this.loading.entity = DEVICE;
      let error = false;
      const ids = this.currentPageDeviceIds.map((d) => d.device_id);

      try {
        const response = await axios.post(this.customerPLCDeviceInfoEndPoint, {
          ring_devices: ids,
        });
        this.tableDevices = get(response.data, "ring_devices", [])
          // plc device profile
          .map((dp) => {
            const device_id = dp.mac_id || dp.device_id;
            let dr = this.deviceRegistry[device_id];
            const { last_requests, state, name, type: kind } = dp;

            delete dp.last_requests; // TODO: check

            return {
              ...dr,
              device_id,
              profile: dp,
              name,
              description: dp.product.description,
              state,
              kind,
              isAccessory: false,
              last_requests,
              warranty: {},
              eligibility: DeviceWarrantyAndEligibilityPolicy.create(null),
            };
          })
          .filter((dp) => dp !== null);
      } catch (err) {
        console.log(err);
        this.error = err.data;
        error = true;
      } finally {
        this.loading.status = false;
        this.loading.entity = null;
      }
    },
    groupDevicesByLocation(devices) {
      return devices.reduce((acc, device) => {
        const locationId = device.location_id;
        if (!acc[locationId]) {
          acc[locationId] = [];
        }
        acc[locationId].push({
          device_id: device.device_id,
          doorbot_id: device.doorbot_id,
        });
        return acc;
      }, {});
    },
    async fetchDevicesWarrantyAndEligibility(entity, ids) {
      if (ids.length === 0) return;

      let devices = ids.map((identifier) => ({
        device_id: identifier.device_id,
        doorbot_id: identifier.doorbot_id,
        location_id: identifier.location_id,
      }));

      const request = this.http.coverage[entity];
      request.fetching = true;

      let targetTable;
      let devicesPerLocation = undefined;

      switch (entity) {
        case ACCESSORY:
          targetTable = this.tableAccessories;
          break;
        case DEVICE:
          targetTable = this.tableDevices;
          devicesPerLocation = this.groupDevicesByLocation(devices);
          break;
      }

      try {
        let coverage;
        if (devicesPerLocation !== undefined) {
          const locationCoverages = await Promise.all(
            Object.entries(devicesPerLocation).map(
              async ([locationId, devices]) => {
                return await this.getWarrantyAndEligibilityStatus({
                  devices,
                  customerId: this.customer.id,
                  category: entity,
                  locationId,
                });
              }
            )
          );
          coverage = Object.assign({}, ...locationCoverages);
        } else {
          coverage = await this.getWarrantyAndEligibilityStatus({
            devices,
            customerId: this.customer.id,
            category: entity,
          });
        }

        targetTable.forEach((d) => {
          let warranty = coverage[d.device_id]?.warranty;
          const eligibilityPolicy = DeviceWarrantyAndEligibilityPolicy.create(
            coverage[d.device_id],
            d.isAccessory
          );
          this.$set(d, "eligibility", eligibilityPolicy);

          if (!warranty) {
            const w = new Warranty();
            warranty = w.toJson();
          }
          this.$set(d, "warranty", warranty || {});
          this.$set(d, "warranty_status", warranty.status || "Not Available");
        });

        request.loaded = true;
      } catch (err) {
        console.error(err);
        const w = new Warranty().toJson();

        for (const d of this.tableDevices) {
          const eligibilityPolicy =
            DeviceWarrantyAndEligibilityPolicy.create(null);
          this.$set(d, "eligibility", eligibilityPolicy);
          this.$set(d, "warranty", w);
          this.$set(d, "warranty_status", w.status);
        }
      }
      request.fetching = false;
    },
    parsePlcDevices(customer) {
      this.accessories = get(customer, "accessories", [])
        .map((acc) => {
          const { product, current_stock, last_requests, profile_id: id } = acc;
          acc.isAccessory = true;
          acc.kind = acc.type;
          acc.state = get(acc, "state", "---");
          acc.name = acc.product.description;
          acc.profile = { product, current_stock, last_requests, id };
          acc.eligibility = DeviceWarrantyAndEligibilityPolicy.create(null);
          return acc;
        })
        .filter((acc) => acc !== null);

      this.totalAccessories = this.accessories.length;
      this.updateDisplayedAccessories();

      this.deviceRegistry = get(customer, "ring_devices", []).reduce(
        (devices, device) => {
          devices[device.device_id] = device;
          return devices;
        },
        {}
      );
      this.totalDevices = this.devices.length;
      this.updateDisplayedDevices();
    },
    fetchCustomerDevices() {
      this.loading.status = true;
      axios
        .get(this.customerPLCDevicesEndPoint)
        .then((response) => {
          try {
            this.parsePlcDevices(response.data);
          } catch (e) {
            console.error(e);
          }
        })
        .catch((error) => {
          console.log(error);
          this.error = error.data;
        });
    },
    refundForm(device) {
      const { eligibility } = device;
      const { refund } = eligibility;
      const needByPass = refund.isRestricted && refund.isUserAllowed;

      const openRefundForm = (oowBypassReason = null) => {
        this.currentDevice = device;
        this.$nextTick(() => {
          this.$refs.refund.openForm(oowBypassReason);
        });
      };

      if (needByPass) {
        this.$nextTick(() => {
          this.$refs.outOfWarrantyWarningModal.show(
            "refund",
            eligibility,
            openRefundForm
          );
        });
      } else if (refund.isUserAllowed) {
        openRefundForm();
      }
    },
    replaceForm(device) {
      const { eligibility, warranty } = device;
      const { replacement } = eligibility;
      const needByPass = replacement.isRestricted && replacement.isUserAllowed;

      const showReplacementForm = () => {
        this.currentDevice = device;
        this.replacementFormVisible = true;
      };

      if (needByPass) {
        showReplacementForm();
        if (device.isAccessory) return;

        const {
          activation_date: firstSetupDate,
          standard_expiration: warrantyEndsAt,
          notes: oowReason,
        } = warranty;

        this.firstSetupDate = firstSetupDate;
        this.warrantyEndsAt = warrantyEndsAt;
        this.oowReason = oowReason;
      } else if (replacement.isUserAllowed) showReplacementForm();
    },
    partsForm(device) {
      const { eligibility } = device;
      const { parts } = eligibility;
      const needByPass = parts.isRestricted && parts.isUserAllowed;

      const showPartsForm = (oowBypassReason = null) => {
        this.currentDevice = device;
        this.$nextTick(() => {
          this.$refs.parts.openForm(oowBypassReason);
        });
      };

      if (needByPass) {
        this.$refs.outOfWarrantyWarningModal.show(
          "parts",
          eligibility,
          showPartsForm
        );
      } else if (parts.isUserAllowed) showPartsForm();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/newui/buttons.scss";

.new-ui__left-nav .nav-action.el-button--primary.is-round {
  @include nav-bar__button();
}
</style>

<style lang="scss">
.inline {
  display: inline-block;
}

.modal-devices {
  margin: 0px;
  padding: 0px;
}

.modal-devices-header {
  display: flex;
  padding: 15px 0px !important;
  align-items: center;
  justify-content: space-between;
  margin: 0px !important;
  font-weight: bold;
}

.modal-device-row {
  float: unset !important;
  display: flex;
  border-bottom: dashed 1px #eee;
  padding: 15px 0px !important;
  align-items: center;
  justify-content: space-between;
  margin: 0px !important;
}

.oow-text {
  margin: 0 1rem 0 1rem;
  display: inline-flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: baseline;

  &span {
    flex: auto;
  }
}
</style>
